import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login({ onLogin }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isFirstUser, setIsFirstUser] = useState(false);
  const [checkingUsers, setCheckingUsers] = useState(true);
  const [formData, setFormData] = useState({ 
    username: '', 
    email: '', 
    password: '', 
    confirmPassword: ''
  });

  // Check if any users exist in the system
  useEffect(() => {
    const checkUserExists = async () => {
      try {
        const response = await axios.get(`${API}/auth/check-users`);
        setIsFirstUser(response.data.user_count === 0);
      } catch (error) {
        console.error('Error checking users:', error);
        setIsFirstUser(false);
      } finally {
        setCheckingUsers(false);
      }
    };
    checkUserExists();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isFirstUser) {
      // First user registration
      if (formData.password !== formData.confirmPassword) {
        toast.error('Passwörter stimmen nicht überein');
        return;
      }
      
      setIsLoading(true);
      try {
        const response = await axios.post(`${API}/auth/register`, {
          username: formData.username,
          email: formData.email || undefined,
          password: formData.password
        });
        toast.success('Administrator erfolgreich erstellt!');
        onLogin(response.data.access_token, response.data.user);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Registrierung fehlgeschlagen');
      } finally {
        setIsLoading(false);
      }
    } else {
      // Regular login
      setIsLoading(true);
      try {
        const response = await axios.post(`${API}/auth/login`, {
          username: formData.username,
          password: formData.password
        });
        toast.success('Erfolgreich angemeldet!');
        onLogin(response.data.access_token, response.data.user);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Anmeldung fehlgeschlagen');
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (checkingUsers) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0A1F33 0%, #00B4D8 50%, #0096C7 100%)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white">Wird geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #0A1F33 0%, #00B4D8 50%, #0096C7 100%)' }}>
      <Card className="w-full max-w-md glass-effect" data-testid="login-card">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0A1F33 0%, #00B4D8 100%)' }}>
              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
          <CardTitle className="text-2xl text-center font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {isFirstUser ? 'Administrator einrichten' : 'eDeliveryNote'}
          </CardTitle>
          <CardDescription className="text-center">
            {isFirstUser 
              ? 'Erstellen Sie den ersten Administrator-Account' 
              : 'Digitale Lieferschein-Verwaltung mit Signatur'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isFirstUser && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
              <p className="text-sm text-blue-800 font-medium">
                🔐 Dies ist die Ersteinrichtung. Der erste Benutzer wird automatisch zum Administrator mit allen Rechten.
              </p>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Benutzername *</Label>
              <Input
                id="username"
                data-testid="username-input"
                type="text"
                placeholder="Benutzername eingeben"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
              />
            </div>
            
            {isFirstUser && (
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail (optional)</Label>
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  placeholder="ihre@email.de"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="password">Passwort *</Label>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                placeholder="Passwort eingeben"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
              />
            </div>
            
            {isFirstUser && (
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Passwort bestätigen *</Label>
                <Input
                  id="confirm-password"
                  data-testid="confirm-password-input"
                  type="password"
                  placeholder="Passwort bestätigen"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required
                />
              </div>
            )}
            
            <Button 
              type="submit" 
              className="w-full metaxon-primary text-white" 
              disabled={isLoading} 
              data-testid="submit-btn"
            >
              {isLoading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {isFirstUser ? 'Wird erstellt...' : 'Wird angemeldet...'}</>
              ) : (
                isFirstUser ? 'Administrator erstellen' : 'Anmelden'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
